import type { InclusionScalarValueDelta } from "app/pages/sales-intelligence/domain/inclusion/types";
import { type InclusionOption, inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";

const unique = (values: readonly string[]): readonly string[] => [...new Set(values)];

export type InclusionConditionDto = {
    readonly inclusion: InclusionOption;
};

export const isExcludeInclusionAvailable = <T extends InclusionConditionDto>(
    conditions: readonly T[],
    editing: T | null,
): boolean => {
    if (editing?.inclusion === inclusionOptionMap.excludeOnly) {
        return true;
    }

    return !conditions.some((condition) => condition.inclusion === inclusionOptionMap.excludeOnly);
};

export const enforceAtMostOneExcludeCondition = <T extends InclusionConditionDto>(conditions: readonly T[]): T[] => {
    let hasExcludeCondition = false;

    return conditions.filter((condition) => {
        if (condition.inclusion !== inclusionOptionMap.excludeOnly) {
            return true;
        }

        if (hasExcludeCondition) {
            return false;
        }

        hasExcludeCondition = true;
        return true;
    });
};

const haveScalarOverlap = (left: readonly string[], right: readonly string[]): boolean =>
    left.some((scalar) => right.includes(scalar));

export const calculateInclusionConditionsScalarDelta = <T extends InclusionConditionDto>(
    previousValue: readonly T[] | undefined,
    newValue: readonly T[] | undefined,
    getScalars: (item: T) => readonly string[],
    isSameConditionBeingEdited: (left: T, right: T) => boolean,
): InclusionScalarValueDelta => {
    const previousConditions = previousValue ?? [];
    const newConditions = [...(newValue ?? [])];
    const removedScalars: string[] = [];

    for (const previousCondition of previousConditions) {
        const matchIndex = newConditions.findIndex(
            (nextCondition) =>
                nextCondition.inclusion === previousCondition.inclusion &&
                (isSameConditionBeingEdited(previousCondition, nextCondition) ||
                    haveScalarOverlap(getScalars(previousCondition), getScalars(nextCondition))),
        );

        if (matchIndex === -1) {
            removedScalars.push(...getScalars(previousCondition));
            continue;
        }

        const matchedCondition = newConditions[matchIndex];
        const nextScalars = new Set(getScalars(matchedCondition));

        removedScalars.push(...getScalars(previousCondition).filter((scalar) => !nextScalars.has(scalar)));

        if (isSameConditionBeingEdited(previousCondition, matchedCondition)) {
            newConditions.splice(matchIndex, 1);
        }
    }

    return { deleted: unique(removedScalars) };
};

export const isInclusionConditionFullyRemoved = (
    conditionScalars: readonly string[],
    valueDelta?: InclusionScalarValueDelta,
): boolean => {
    if (!valueDelta?.deleted?.length || conditionScalars.length === 0) {
        return false;
    }

    const removed = new Set(valueDelta.deleted);

    return conditionScalars.every((scalar) => removed.has(scalar));
};
